import { readFileSync } from "node:fs";
import { Command, Option } from "clipanion";
import { DefaultAzureCredential, ManagedIdentityCredential, type TokenCredential } from "@azure/identity";
import type { InstalledStamp } from "@m8t-stack/platform-release";
import { M8tCommand } from "../../lib/m8t-command.js";
import { LocalCliError } from "../../lib/errors.js";
import { CLI_VERSION } from "../../lib/package-version.js";
import { readStamp, writeStamp } from "../../lib/platform-stamp.js";
import { discoverStampStorage } from "../../lib/platform-storage-discovery.js";
import { buildSeedStamp, isBarePlatformVersion, validateDescriptor, type InstallDescriptor } from "../../lib/platform-stamp-seed.js";
import { renderJson, resolveOutputMode } from "../../lib/output.js";

export interface SeedStampArgs {
  descriptorPath: string;
  /**
   * Bare X.Y.Z the caller says this install IS, overriding the descriptor's baked
   * value. Absent ⇒ the descriptor's value, which is the documented fallback.
   */
  platformVersion?: string;
  credential: TokenCredential;
  subscriptionId: string;
  resourceGroup: string;
  nowIso?: string;
  readFileImpl?: (p: string) => string;
  readStampImpl?: (o: { credential: TokenCredential; subscriptionId: string; resourceGroup: string }) => Promise<InstalledStamp | null>;
  writeStampImpl?: (s: InstalledStamp, o: { credential: TokenCredential; subscriptionId: string; resourceGroup: string; onProgress?: (m: string) => void }) => Promise<void>;
  discoverImpl?: (o: { credential: TokenCredential; subscriptionId: string; resourceGroup: string }) => Promise<{ tableEndpoint: string; accountResourceId: string; accountName: string }>;
  onProgress?: (m: string) => void;
}

export interface SeedStampResult {
  outcome: "seeded" | "seeded-partial" | "already-stamped";
  platformVersion: string;
  storageAccount: string;
  components?: Record<string, string>;
}

/**
 * Seed the installed stamp from a descriptor — ONLY IF ABSENT.
 *
 * The only-if-absent guard is the whole idempotency story: a re-run, an installer
 * container restart, or a second call after a real converge must never clobber a stamp
 * that another writer owns. The converge engine remains the sole authority for updates.
 */
export async function seedStamp(args: SeedStampArgs): Promise<SeedStampResult> {
  // Ahead of every read and every network call: a malformed override is a caller
  // bug, and the one thing that must never happen is a half-parsed version landing
  // in the stamp — that row is what the updater diffs against and what fleet
  // telemetry aggregates. REFUSE rather than fall back: the installer's caller
  // (installer/lib.sh) only ever passes a value it has already normalized, so this
  // is unreachable from the paved path, and a silent fallback here would hide a
  // real defect in whichever launcher got it wrong.
  if (args.platformVersion !== undefined && !isBarePlatformVersion(args.platformVersion)) {
    throw new LocalCliError({
      code: "PLATFORM_SEED_VERSION_INVALID",
      message: `--platform-version must be a bare X.Y.Z release version, got ${JSON.stringify(args.platformVersion)}.`,
      hint: "Pass the platform release this install is deploying, e.g. --platform-version 0.7.8. Omit the flag to stamp the version baked into the installer image.",
    });
  }
  const readFileImpl = args.readFileImpl ?? ((p: string) => readFileSync(p, "utf8"));
  const readStampImpl = args.readStampImpl ?? readStamp;
  const writeStampImpl =
    args.writeStampImpl ??
    ((stamp: InstalledStamp, o: { credential: TokenCredential; subscriptionId: string; resourceGroup: string; onProgress?: (m: string) => void }) =>
      writeStamp({ ...o, stamp }));
  const discoverImpl = args.discoverImpl ?? discoverStampStorage;
  const ctx = { credential: args.credential, subscriptionId: args.subscriptionId, resourceGroup: args.resourceGroup };

  let raw: unknown;
  try {
    raw = JSON.parse(readFileImpl(args.descriptorPath));
  } catch (e) {
    throw new LocalCliError({
      code: "PLATFORM_SEED_DESCRIPTOR_UNREADABLE",
      message: `Cannot read the install descriptor at ${args.descriptorPath}.`,
      hint: "The descriptor is generated at installer-image build time (scripts/install-descriptor-gen.mjs). An image built without that step cannot seed a stamp.",
      cause: e,
    });
  }
  const errors = validateDescriptor(raw);
  if (errors.length > 0) {
    throw new LocalCliError({
      code: "PLATFORM_SEED_DESCRIPTOR_INVALID",
      message: `The install descriptor at ${args.descriptorPath} is not valid: ${errors.join("; ")}.`,
      hint: "Regenerate it with scripts/install-descriptor-gen.mjs.",
    });
  }
  const descriptor = raw as InstallDescriptor;

  const { accountName } = await discoverImpl(ctx);
  const existing = await readStampImpl(ctx);
  if (existing) {
    args.onProgress?.(`platform stamp already present (${existing.platformVersion}) — leaving it untouched.`);
    return { outcome: "already-stamped", platformVersion: existing.platformVersion, storageAccount: accountName };
  }

  const stamp = buildSeedStamp(descriptor, CLI_VERSION, args.nowIso ?? new Date().toISOString(), args.platformVersion);
  await writeStampImpl(stamp, { ...ctx, onProgress: args.onProgress });

  return {
    outcome: descriptor.content ? "seeded" : "seeded-partial",
    platformVersion: stamp.platformVersion,
    storageAccount: accountName,
    components: {
      gateway: stamp.components.gateway.tag,
      codingAgent: stamp.components.codingAgent.tag,
      azureExecutor: stamp.components.azureExecutor.tag,
    },
  };
}

export class PlatformSeedStampCommand extends M8tCommand {
  static paths = [["platform", "seed-stamp"]];
  static usage = Command.Usage({
    description: "INTERNAL: write the initial installed-state stamp from a build-time install descriptor.",
    details:
      "Used by the cloud installer at the end of a from-zero install so the platform records which release it is running. Writes the stamp ONLY IF ABSENT — it never overwrites a stamp written by the converge engine, so a re-run or a container restart is harmless. Not a founder-facing command: to change what is installed, use 'm8t platform update'.",
  });

  descriptor = Option.String("--descriptor", { description: "Path to install-descriptor.json (baked into the installer image)." });
  platformVersion = Option.String("--platform-version", { description: "Bare X.Y.Z release this install is deploying. Overrides the descriptor's baked version, which lags by one declaration under publish->select. Omit to use the baked value." });
  subscription = Option.String("--subscription");
  resourceGroup = Option.String("--resource-group");
  miClientId = Option.String("--mi-client-id", { description: "Authenticate as this user-assigned managed identity instead of the ambient credential." });
  output = Option.String("--output");

  async executeCommand(): Promise<number> {
    const mode = resolveOutputMode(this.output as "pretty" | "json" | "auto" | undefined, this.context.stdout as NodeJS.WriteStream);
    const need = (v: unknown, flag: string): string => {
      if (typeof v !== "string" || v.trim().length === 0) {
        throw new LocalCliError({ code: "PLATFORM_SEED_ARG_MISSING", message: `${flag} is required.` });
      }
      return v.trim();
    };
    // Progress goes to STDERR so `--output json` keeps a clean, parseable stdout.
    const onProgress = (m: string) => { this.context.stderr.write(`${m}\n`); };

    const credential: TokenCredential =
      typeof this.miClientId === "string" && this.miClientId.trim().length > 0
        ? new ManagedIdentityCredential({ clientId: this.miClientId.trim() })
        : new DefaultAzureCredential();

    const result = await seedStamp({
      descriptorPath: need(this.descriptor, "--descriptor"),
      // Absent flag ⇒ undefined ⇒ the descriptor's baked value. A flag PASSED but
      // empty is not the same thing and is refused below, deliberately: the
      // installer omits the flag entirely when it has nothing to say
      // (installer/lib.sh), so an empty one means a launcher computed a version and
      // got nothing — which should be loud, not silently absorbed by the fallback.
      platformVersion: typeof this.platformVersion === "string" ? this.platformVersion.trim() : undefined,
      credential,
      subscriptionId: need(this.subscription, "--subscription"),
      resourceGroup: need(this.resourceGroup, "--resource-group"),
      onProgress,
    });

    if (mode === "json") {
      this.context.stdout.write(renderJson(result) + "\n");
    } else {
      this.context.stdout.write(`${result.outcome}: ${result.platformVersion} (storage ${result.storageAccount})\n`);
    }
    return 0;
  }
}
